import { z } from "zod";
import {
  authorityLevelEnum,
  sourceCategoryEnum,
  sourceTypeEnum,
} from "../../types/enums.js";
import { entityBaseSchema } from "./common.js";

/**
 * Source frontmatter — domain source reference (regulation, standard, policy).
 *
 * Local `iamu/sources/*.md` files are accepted as raw markdown. Semantic
 * search + hybrid ranking still require cloud KB; local markdown is just for
 * direct inclusion in prompts or reference in walkthroughs.
 */
export const sourceFrontmatterSchema = entityBaseSchema.extend({
  /** Corresponds to cloud `name`. */
  title: z.string().min(1),
  source_type: sourceTypeEnum.default("manual"),
  category: sourceCategoryEnum.nullish(),
  authority_level: authorityLevelEnum,
  /** Exactly one of `url` or `path` MUST be present. */
  url: z.string().url().nullish(),
  /** Filesystem path relative to `iamu/sources/` for inlined source text. */
  path: z.string().nullish(),
  language: z.string().nullish(),
  /** Whether the citation-and-authority context has been human-verified. */
  verified: z.boolean().default(false),
});

export type SourceFrontmatter = z.infer<typeof sourceFrontmatterSchema>;

/**
 * Runtime-validated shape: enforce **exactly one** (XOR) of `url` or `path`
 * at parse time. Having neither is a missing authoritative reference; having
 * both is ambiguous — consumers don't know which is authoritative, so downstream
 * citations could differ by call site.
 */
export const sourceFrontmatterRefined = sourceFrontmatterSchema.superRefine(
  (val, ctx) => {
    const hasUrl = typeof val.url === "string" && val.url.length > 0;
    const hasPath = typeof val.path === "string" && val.path.length > 0;
    if (!hasUrl && !hasPath) {
      const msg = "source MUST have exactly one of `url` or `path`";
      ctx.addIssue({ code: "custom", message: msg, path: ["url"] });
      ctx.addIssue({ code: "custom", message: msg, path: ["path"] });
    } else if (hasUrl && hasPath) {
      const msg =
        "source MUST have exactly one of `url` or `path` — pick the authoritative reference";
      ctx.addIssue({ code: "custom", message: msg, path: ["url"] });
      ctx.addIssue({ code: "custom", message: msg, path: ["path"] });
    }
  },
);
